import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, CreditCard, Loader2, Receipt, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const fr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');
const STATUTS = {
  paye: ['Payée', 'bg-emerald-100 text-emerald-800'],
  en_cours: ['Prélèvement en cours', 'bg-sky-100 text-sky-800'],
  echec: ['Rejetée', 'bg-rose-100 text-rose-800'],
  represente_programme: ['Représentation programmée', 'bg-violet-100 text-violet-800'],
  a_prelever: ['À prélever', 'bg-amber-100 text-amber-800'],
  a_venir: ['À venir', 'bg-slate-100 text-slate-700'],
};

export const Representer = ({ e, onValider, fraisEur }) => {
  const [ouvert, setOuvert] = useState(false);
  const demain = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [jour, setJour] = useState(demain);
  if (!ouvert) {
    return <button type="button" onClick={() => setOuvert(true)} className="inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700" data-testid={`echeance-representer-${e.numero}`}><RotateCcw className="h-3 w-3" /> Représenter</button>;
  }
  return (
    <span className="inline-flex items-center gap-1">
      <input type="date" min={new Date().toISOString().slice(0, 10)} value={jour} onChange={(ev) => setJour(ev.target.value)} className="rounded border border-slate-300 px-1.5 py-0.5 text-[11px]" data-testid={`echeance-representer-date-${e.numero}`} />
      <button type="button" onClick={() => onValider(e, jour).then((ok) => ok && setOuvert(false))} className="rounded bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white" data-testid={`echeance-representer-valider-${e.numero}`}>Programmer + avertir{fraisEur ? ` (+ ${eur(fraisEur)} de frais)` : ''}</button>
    </span>
  );
};

export const representerPrelevement = async (base, headers, e, jour, apres) => {
  try {
    await axios.post(`${base}/prelevements/${e.numero}/representer`, { date: jour }, { headers });
    toast.success(`Représentation programmée le ${fr(jour)} — le membre est prévenu par e-mail et SMS`);
    apres();
    return true;
  } catch (err) { toast.error(err.response?.data?.detail || 'Représentation impossible'); return false; }
};

/** Liste des échéances d'un mandat : payées, en cours, rejetées, à venir, avec reçu. `admin` active la représentation. */
export const EcheancesMandat = ({ mandatId, headers, admin = false }) => {
  const [items, setItems] = useState(null);
  const base = admin ? `${API}/sepa-stripe/admin/mandats/${mandatId}` : `${API}/sepa-stripe/membre/mandats/${mandatId}`;
  const auth = headers?.Authorization;
  const charger = useCallback(async () => {
    try { setItems((await axios.get(`${base}/echeances`, { headers: { Authorization: auth } })).data.echeances || []); } catch { setItems([]); }
  }, [base, auth]);
  useEffect(() => { charger(); }, [charger]);

  const recu = async (e) => {
    const url = admin ? `${API}/comptabilite/documents/${e.recu_ged_id}/download` : `${base}/recus/${e.recu_ged_id}`;
    try {
      const r = await axios.get(url, { headers, responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Reçu indisponible'); }
  };
  const representer = async (e, jour) => representerPrelevement(base, headers, e, jour, charger);

  if (!items) return <Loader2 className="h-4 w-4 animate-spin text-slate-400" />;
  if (!items.length) return null;
  return (
    <div className="rounded-lg border border-slate-200" data-testid={`echeances-mandat-${mandatId}`}>
      <p className="flex items-center gap-1.5 border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-semibold text-[#0a3d62]"><CalendarClock className="h-3.5 w-3.5" /> Échéances du mandat</p>
      <ul className="divide-y divide-slate-100">
        {items.map((e) => {
          const [lib, cls] = STATUTS[e.statut] || STATUTS.a_venir;
          return (
            <li key={e.numero} className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs" data-testid={`echeance-${mandatId}-${e.numero}`}>
              <span className="w-8 font-mono text-slate-500">n°{e.numero}</span>
              <span className="w-24">{fr(e.date)}</span>
              <span className="w-24 font-semibold">{eur(e.montant)}</span>
              <span className={`rounded-full px-2 py-0.5 font-semibold ${cls}`} data-testid={`echeance-statut-${mandatId}-${e.numero}`}>
                {lib}{e.statut === 'represente_programme' ? ` le ${fr(e.date_representation)}` : ''}{e.tentative > 1 && e.statut !== 'represente_programme' ? ` (tentative ${e.tentative})` : ''}
              </span>
              {admin && e.statut === 'echec' && e.motif && <span className="text-rose-700">{e.motif}</span>}
              {e.frais && <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${e.frais.statut === 'paye' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid={`echeance-frais-${mandatId}-${e.numero}`}>Frais de rejet {eur(e.frais.montant)} — {{ a_prelever: 'à prélever à la représentation', en_cours: 'prélèvement en cours', paye: 'payés', echec: 'rejetés' }[e.frais.statut] || e.frais.statut}</span>}
              <span className="ml-auto flex items-center gap-1.5">
                {e.recu_ged_id && <button type="button" onClick={() => recu(e)} className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 font-semibold text-emerald-800 hover:bg-emerald-100" data-testid={`echeance-recu-${mandatId}-${e.numero}`}><Receipt className="h-3 w-3" /> Reçu {e.recu_numero || ''}</button>}
                {!admin && e.lien_carte && <a href={e.lien_carte} className="inline-flex items-center gap-1 rounded bg-[#0a3d62] px-2 py-1 font-semibold text-white hover:bg-[#0d4f7e]" data-testid={`echeance-payer-carte-${mandatId}-${e.numero}`}><CreditCard className="h-3 w-3" /> Payer par carte {eur(e.a_regler_carte)}</a>}
                {admin && e.statut === 'echec' && <Representer e={e} onValider={representer} fraisEur={e.frais && e.frais.statut !== 'paye' ? e.frais.montant : 0} />}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
